<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  card: { type: Object, required: true },
  bubbleText: { type: String, default: '' },
});

const PRODUCT_ACTION_LABELS = {
  more_info: 'Más información',
  add_to_cart: 'Añadir al carrito',
};
const interactiveActionsHint =
  'Acciones interactivas disponibles para el cliente en el canal.';

const visibleTitle = computed(() => {
  if (!props.card.title) {
    return '';
  }

  return props.card.title === props.bubbleText ? '' : props.card.title;
});

const primaryLink = computed(() => {
  return props.card.actions.find(action => action.uri)?.uri || '';
});

const hasInteractiveActions = computed(() => {
  return props.card.actions.some(action => !action.uri);
});

const parseProductAction = payload => {
  if (!payload) {
    return null;
  }

  try {
    const parsed = JSON.parse(payload);
    return parsed?.type === 'product_action' ? parsed : null;
  } catch {
    return null;
  }
};

const actionLabel = action => {
  if (action.text || action.title) {
    return action.text || action.title;
  }

  const parsedPayload = parseProductAction(action.payload);
  if (parsedPayload?.action && PRODUCT_ACTION_LABELS[parsedPayload.action]) {
    return PRODUCT_ACTION_LABELS[parsedPayload.action];
  }

  return action.uri ? 'Ver producto' : 'Acción interactiva';
};

const actionIcon = action => {
  if (action.uri) {
    return 'i-lucide-arrow-up-right';
  }

  const parsedPayload = parseProductAction(action.payload);
  if (parsedPayload?.action === 'add_to_cart') {
    return 'i-lucide-shopping-cart';
  }

  return 'i-lucide-info';
};
</script>

<template>
  <article
    class="flex h-full flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-alpha-black2 shadow-sm"
  >
    <a
      v-if="primaryLink"
      :href="primaryLink"
      target="_blank"
      rel="noopener nofollow noreferrer"
      class="block border-b border-n-weak bg-white"
    >
      <img
        v-if="card.imageUrl"
        :src="card.imageUrl"
        :alt="card.title || bubbleText"
        class="h-48 w-full object-contain"
      />
      <div
        v-else
        class="flex h-48 items-center justify-center bg-n-slate-2 text-n-slate-10"
      >
        <Icon icon="i-lucide-image-off" class="text-2xl" />
      </div>
    </a>

    <div v-else class="border-b border-n-weak bg-white">
      <img
        v-if="card.imageUrl"
        :src="card.imageUrl"
        :alt="card.title || bubbleText"
        class="h-48 w-full object-contain"
      />
      <div
        v-else
        class="flex h-48 items-center justify-center bg-n-slate-2 text-n-slate-10"
      >
        <Icon icon="i-lucide-image-off" class="text-2xl" />
      </div>
    </div>

    <div class="flex flex-1 flex-col gap-3 p-4">
      <div class="flex flex-col gap-1.5">
        <a
          v-if="visibleTitle && primaryLink"
          :href="primaryLink"
          target="_blank"
          rel="noopener nofollow noreferrer"
          class="line-clamp-2 text-sm font-semibold text-n-slate-12 hover:text-n-blue-11"
        >
          {{ visibleTitle }}
        </a>

        <div
          v-else-if="visibleTitle"
          class="line-clamp-2 text-sm font-semibold text-n-slate-12"
        >
          {{ visibleTitle }}
        </div>

        <div
          v-if="card.description"
          class="line-clamp-3 whitespace-pre-line text-sm text-n-slate-11"
        >
          {{ card.description }}
        </div>
      </div>

      <div class="mt-auto flex flex-col gap-2.5">
        <div v-if="card.actions.length" class="flex flex-col gap-2">
          <template
            v-for="(action, actionIndex) in card.actions"
            :key="`${action.uri || action.payload || action.text || actionIndex}`"
          >
            <a
              v-if="action.uri"
              :href="action.uri"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="inline-flex h-9 items-center justify-center gap-2 rounded-xl bg-n-brand px-3 text-sm font-medium text-white transition-colors hover:brightness-110"
            >
              <Icon :icon="actionIcon(action)" class="text-base" />
              <span class="truncate">{{ actionLabel(action) }}</span>
            </a>

            <button
              v-else
              type="button"
              disabled
              class="inline-flex h-9 cursor-default items-center justify-center gap-2 rounded-xl border border-n-weak bg-n-alpha-2 px-3 text-sm font-medium text-n-slate-12"
            >
              <Icon :icon="actionIcon(action)" class="text-base" />
              <span class="truncate">{{ actionLabel(action) }}</span>
            </button>
          </template>
        </div>

        <div v-if="hasInteractiveActions" class="text-xs text-n-slate-10">
          {{ interactiveActionsHint }}
        </div>
      </div>
    </div>
  </article>
</template>
